// src/components/AIReportDisplay3.js

// --- 1. UPDATE IMPORTS ---
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle } from 'lucide-react'; // For loading/error UI
import './AIReportDisplay2.css'; // Assuming you use the same CSS
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Counselling from './Counselling';
import { Helmet } from 'react-helmet-async';
import studentImage from '../assets/images/student.jpg';

const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;

// All helper components (formatToLakhs, QnAItem, ScholarshipCard, etc.) remain unchanged...
// ... (Your helper components go here) ...
const formatToLakhs = (num) => {
    if (num === null || num === undefined || num === 'N/A' || num === '') {
        return 'N/A';
    }
    const numStr = String(num);
    // If it's already formatted as Lakhs/Crore, just clean and return
    if (numStr.toLowerCase().includes('lakh') || numStr.toLowerCase().includes('crore')) {
        return `₹ ${numStr.replace(/INR/gi, '').trim()}`;
    }
    // Extract numbers from strings like "INR 8,00,000"
    const number = Number(numStr.replace(/[^0-9.-]+/g, ""));
    if (isNaN(number)) {
        return num; // Return original string if it's not a parsable number
    }
    if (number >= 10000000) {
        const value = (number / 10000000).toFixed(2).replace(/\.00$/, '');
        return `₹ ${value} Crore`;
    } else if (number >= 100000) {
        const value = (number / 100000).toFixed(2).replace(/\.00$/, '');
        return `₹ ${value} Lakh`;
    }
    return `₹ ${number.toLocaleString('en-IN')}`;
};
const QnAItem = ({ item }) => (
    <div className="qna-item">
        <p className="qna-question">{item?.question || 'N/A'}</p>
        <p className="qna-answer">{item?.answer || 'N/A'}</p>
    </div>
);
const ScholarshipCard = ({ scholarships }) => {
    if (!scholarships || scholarships.length === 0) return null;

    return (
        <div className="data-card">
            <h3 className="data-card__title"><i className='bx bxs-award'></i>Scholarship Details</h3>
            <div className="scroll-section-wrapper">
                {scholarships.map((scholarship, index) => (
                    <div key={index} className="scholarship-item qna-item">
                        <h4 className="scholarship-name">{scholarship.name}</h4>
                        <p><span className="text-accent">Amount:</span> {formatToLakhs(scholarship.amount?.inr)} ({scholarship.amount?.local})</p>
                        <p><span className="text-accent">Criteria:</span> {scholarship.criteria}</p>
                        <p><span className="text-accent">How to Claim:</span> {scholarship.claimInstructions}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};
const KeyValueCard = ({ title, icon, data }) => {
    if (!data) return null;
    const entries = Object.entries(data).filter(([_, value]) => value && value !== 'Not Provided' && value.length > 0);
    if (entries.length === 0) return null;

    const formatKey = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

    return (
        <div className="data-card">
            <h3 className="data-card__title"><i className={`bx ${icon}`}></i>{title}</h3>
            <ul className="profile-detail-list">
                {entries.map(([key, value]) => (
                    <li key={key}><span>{formatKey(key)}</span><strong>{value.toString()}</strong></li>
                ))}
            </ul>
        </div>
    );
};
const CostAnalysis = ({ fullCostOfLiving, educationCosts, primaryRecommendation, totalFirstYearInr, investmentPlanning }) => {
    const [selectedCurrency, setSelectedCurrency] = useState('inr');
    const formatLocalCurrency = (value) => value || 'N/A';
    const costItemsConfig = [
        { key: 'accommodation', label: 'Accommodation' },
        { key: 'cityLivingCost', label: 'City Living Cost' },
        { key: 'transportcost', label: 'Transport Cost' },
        { key: 'foodAndGroceryCost', label: 'Food And Grocery Cost' },
        { key: 'miscellaneous', label: 'Miscellaneous' },
    ];
    const orderedCosts = costItemsConfig.map(config => {
        const dataKey = Object.keys(fullCostOfLiving || {}).find(k => k.toLowerCase().startsWith(config.key));
        if (dataKey && fullCostOfLiving[dataKey]) {
            const value = fullCostOfLiving[dataKey];
            const hasMonthly = dataKey.toLowerCase().includes('monthly');
            return { ...config, value, hasMonthly, dataKey };
        }
        return null;
    }).filter(Boolean);

    return (
        <div className="data-card">
            <h3 className="data-card__title"><i className='bx bx-wallet'></i>Cost Analysis ({primaryRecommendation?.state || 'N/A'})</h3>
            <div className="currency-toggle">
                <button className={`toggle-button ${selectedCurrency === 'inr' ? 'active' : ''}`} onClick={() => setSelectedCurrency('inr')}>INR (₹)</button>
                <button className={`toggle-button ${selectedCurrency === 'local' ? 'active' : ''}`} onClick={() => setSelectedCurrency('local')}>Local</button>
            </div>
            <div className="cost-items-container">
                <div className="cost-analysis-item">
                    <div className="cost-label-group"><span className="cost-label-main">Tuition</span><span className="cost-label-sub">Yearly</span></div>
                    <span className="cost-value">{selectedCurrency === 'inr' ? formatToLakhs(primaryRecommendation?.tuition?.inr) : formatLocalCurrency(primaryRecommendation?.tuition?.local)}</span>
                </div>
                {orderedCosts.map(({ dataKey, label, value, hasMonthly }) => (
                    <div className="cost-analysis-item" key={dataKey}>
                        <div className="cost-label-group"><span className="cost-label-main">{label}</span>{hasMonthly && <span className="cost-label-sub">Monthly</span>}</div>
                        <span className="cost-value">{selectedCurrency === 'inr' ? formatToLakhs(value.inr) : formatLocalCurrency(value.local)}</span>
                    </div>
                ))}
                {educationCosts?.hiddenCosts && <div className="cost-analysis-item"><div className="cost-label-group"><span className="cost-label-main">Hidden Costs</span></div><span className="cost-value-description">{educationCosts.hiddenCosts}</span></div>}
            </div>
            <hr />
            <div className="cost-analysis-item total"><span className="cost-label-main">Est. First Year</span><span className="cost-value-total">{formatToLakhs(totalFirstYearInr)}</span></div>
            {(investmentPlanning?.paymentAdvice || investmentPlanning?.scholarshipOpportunities) && (
                <div className="cost-advice-section">
                    {investmentPlanning.paymentAdvice && <p><strong>Payment Advice:</strong> {investmentPlanning.paymentAdvice}</p>}
                    {investmentPlanning.scholarshipOpportunities && <p><strong>Scholarships:</strong> {investmentPlanning.scholarshipOpportunities}</p>}
                </div>
            )}
        </div>
    );
};
const DetailedCollegeCard = ({ college, rank, collegeAnalysis }) => {
    if (!college) return null;
    const formatAcceptanceRate = (rate) => {
        if (rate === null || rate === undefined || rate === 'N/A' || rate === '') return 'N/A';
        const num = String(rate).replace(/%/g, '').trim();
        if (!isNaN(num) && num !== '') return `${num}%`;
        return rate;
    };
    return (
        <div className="detailed-college-card data-card">
            <div className="detailed-college-header">
                <span className="rank-badge">#{rank}</span>
                <h3 className="college-name">{college.college || 'N/A'}</h3>
            </div>
            <div className="college-section-wrapper">
                <div className="college-main-details">
                    <p><span className="detail-label">Course:</span><span className="detail-value">{college.courseName || 'N/A'}</span></p>
                    <p><span className="detail-label">Tuition:</span><span className="detail-value">{college.tuition?.local || 'N/A'} | {formatToLakhs(college.tuition?.inr)}</span></p>
                    <p><span className="detail-label">Deadline:</span><span className="detail-value">{college.applicationDeadline || 'N/A'}</span></p>
                    <p><span className="detail-label">Acceptance:</span><span className="detail-value">{formatAcceptanceRate(college.acceptanceRate)}</span></p>
                    <p><span className="detail-label">Success Chance:</span><span className="detail-value">{college.successProbability || 'N/A'}</span></p>
                </div>

                {collegeAnalysis && (
                    <div className="detailed-college-section">
                        <h4 className="data-card__subtitle">College Analysis</h4>
                        <p><strong>Reputation:</strong> {collegeAnalysis.rankingAndReputation || 'N/A'}</p>
                        <p><strong>Curriculum Focus:</strong> {collegeAnalysis.courseCurriculumSpecializations || 'N/A'}</p>
                        <p><strong>Accreditation:</strong> {collegeAnalysis.accreditationRecognition || 'N/A'}</p>
                        <p><strong>Research:</strong> {collegeAnalysis.facultyResearchOpportunities || 'N/A'}</p>
                    </div>
                )}
                <div className="detailed-college-section"><h4 className='data-card__subtitle'>Entry Requirements</h4><p>{college.entryRequirements || 'N/A'}</p></div>
                <div className="detailed-college-section"><h4 className='data-card__subtitle'>Curriculum Summary</h4><p>{college.curriculumSummary || 'N/A'}</p></div>
                <div className="detailed-college-section"><h4 className='data-card__subtitle'>Top Employers</h4><p>{(Array.isArray(college.topEmployers) ? college.topEmployers : [college.topEmployers]).join(', ')}</p></div>
            </div>
        </div>
    );
};
const CounsellingModal = ({ show, onClose }) => {
    if (!show) return null;
    return (
        <div className={`counselling-modal-backdrop ${show ? 'show' : ''}`}>
            <div className="counselling-modal-window">
                <button onClick={onClose} className="counselling-modal-close">×</button>
                <Counselling onBookingSuccess={onClose} isInsideModal={true} />
            </div>
        </div>
    );
};


// --- 2. UPDATE MAIN COMPONENT ---
// It no longer receives the `data` prop.
const AIReportDisplay3 = () => {
    // --- 3. ADD STATE FOR DATA FETCHING, LOADING, AND ERROR ---
    const [searchParams] = useSearchParams();
    const reportId = searchParams.get('id');

    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isMounted, setIsMounted] = useState(false);
    const [isSopExpanded, setIsSopExpanded] = useState(false);
    const [showCounsellingPopup, setShowCounsellingPopup] = useState(false);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);


    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    // --- 4. ADD DATA FETCHING LOGIC ---
    useEffect(() => {
        if (!reportId) {
            setError('No Report ID was found in the URL.');
            setIsLoading(false);
            return;
        }

        const fetchReportData = async () => {
            try {
                const response = await axios.get(`${REACT_APP_API_BASE}/api/reports/single/${reportId}`);
                setReportData(response.data);
            } catch (err) {
                console.error("Failed to fetch report data:", err);
                setError(err.response?.data?.message || 'An error occurred while fetching the report.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchReportData();
    }, [reportId]);


    const toggleSopExpansion = () => setIsSopExpanded(!isSopExpanded);

    // --- 5. UPDATE useMemo TO USE `reportData` FROM STATE ---
    const transformedData = useMemo(() => {
        if (!reportData) return null;

        const aiData = reportData?.aiResults ? reportData.aiResults : reportData;
        if (!aiData || typeof aiData !== 'object' || !aiData.studentInfo) {
            console.error("AIReportDisplay3: Invalid or empty data provided.", aiData);
            return null;
        }

        // ★★★ THIS IS THE FIX ★★★
        // Check for the AI's incorrect key first, then fall back to the correct one.
        const recommendationsSource = aiData["Top 5 CollegeRecommendations"] || aiData.top5CollegeRecommendations || [];

        // Use the new, reliable source variable.
        const recommendations = recommendationsSource.map(rec => ({
            college: rec.collegeName || 'N/A',
            state: aiData.studentInfo?.selectedState || 'N/A',
            courseName: rec.courseName || 'N/A',
            tuition: rec.tuitionFee,
            applicationDeadline: rec.applicationDeadline || 'N/A',
            acceptanceRate: rec.acceptanceRate || 'N/A',
            successProbability: rec.successProbability || 'N/A',
            entryRequirements: rec.entryRequirements || 'N/A',
            curriculumSummary: rec.curriculumSummary || 'N/A',
            topEmployers: rec.topEmployers || [],
        }));
        // ★★★ END OF FIX ★★★

        return {
            studentInfo: aiData.studentInfo || {},
            recommendations: recommendations, // This is now correctly populated
            jobMarket: aiData.jobMarketSnapshot,
            visaAndSOP: aiData.visaAndSOPAdvice,
            livingCosts: {
                totalFirstYear: aiData.timelineInvestmentPlanning?.firstYearTotalCost,
            },
            fullCostOfLiving: aiData.costOfLiving || {},
            accommodationInsights: aiData.livingCostAccommodationInsights,
            flightCostsTips: aiData.flightCostsTips,
            additionalCertifications: aiData.additionalCertifications || [],
            scholarshipDetails: aiData.scholarshipDetails || [],
            timelineInvestmentPlanning: aiData.timelineInvestmentPlanning,
            prPathway: aiData.prPathwayRoadmap,
            finalActionPlan: aiData.finalActionPlan,
            backupPlan: aiData.backupCourseCountry,
            predictedQnA: aiData.predictedQnA,
            finalComment: aiData.finalComment,
            familyBackgroundFinancialViability: aiData.familyBackgroundFinancialViability,
            postStudyWorkAndPR: aiData.postStudyWorkAndPR,
            countrySafetyAndPreference: aiData.countrySafetyAndPreference,
            roi: aiData.returnOnInvestment,
            peerInfluence: aiData.peerParentalInfluence,
            connectivityLifestyle: aiData.travelConnectivityLifestyle,
            admissionRequirements: aiData.admissionEaseAndRequirements,
            careerAlignment: aiData.careerGoalsAlignment,
            collegeAnalysis: aiData.collegeAnalysis,
            educationCosts: aiData.educationCostBreakdown,
            footerResources: aiData.footerResources,
        };
    }, [reportData]);

    useEffect(() => {
        if (transformedData) {
            const mountTimer = setTimeout(() => setIsMounted(true), 100);
            const popupTimer = setTimeout(() => setShowCounsellingPopup(true), 20000);
            return () => {
                clearTimeout(mountTimer);
                clearTimeout(popupTimer);
            };
        }
    }, [transformedData]);

    const handleDownloadReport = async () => {
        // ... (this function remains unchanged)
        const reportElement = document.getElementById('report-container');
        if (!reportElement) return;
        setIsDownloadingPdf(true);
        reportElement.classList.add('pdf-render-mode');
        try {
            const canvas = await html2canvas(reportElement, { scale: 2, useCORS: true, scrollY: -window.scrollY, windowWidth: reportElement.scrollWidth, windowHeight: reportElement.scrollHeight });
            const imgData = canvas.toDataURL('image/png', 1.0);
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
            heightLeft -= pdfHeight;

            while (heightLeft > 0) {
                position -= pdfHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
                heightLeft -= pdfHeight;
            }

            const fileName = `AI-Report-Premium-${transformedData.studentInfo?.name?.replace(/\s+/g, '_') || 'Student'}.pdf`;
            pdf.save(fileName);
        } catch (error) {
            console.error("Error generating PDF: ", error);
            alert("Sorry, there was an error generating the PDF.");
        } finally {
            reportElement.classList.remove('pdf-render-mode');
            setIsDownloadingPdf(false);
        }
    };

    // --- 6. ADD UI FOR LOADING AND ERROR STATES ---
    // This styling assumes you have a `.report-loading-container` class similar to your other components
    if (isLoading) {
        return (
            <div className="report-loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column', gap: '1rem' }}>
                <Loader2 size={48} className="animate-spin" />
                <p>Loading Your Report...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="report-error-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column', gap: '1rem', color: 'red' }}>
                <AlertTriangle size={48} />
                <p>Error: {error}</p>
            </div>
        );
    }

    // This handles the case where data is fetched but is invalid
    if (!transformedData) {
        return (
            <div className="report-error-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column', gap: '1rem', color: 'red' }}>
                <AlertTriangle size={48} />
                <p>The report data is invalid or missing required information.</p>
            </div>
        );
    }

    const {
        studentInfo, recommendations, jobMarket, visaAndSOP, livingCosts, educationCosts, prPathway, finalActionPlan,
        backupPlan, predictedQnA, finalComment, footerResources, scholarshipDetails, collegeAnalysis, familyBackgroundFinancialViability,
        additionalCertifications, postStudyWorkAndPR, countrySafetyAndPreference, fullCostOfLiving, accommodationInsights,
        flightCostsTips, timelineInvestmentPlanning, admissionRequirements, careerAlignment, roi, peerInfluence, connectivityLifestyle
    } = transformedData;

    const allRecommendations = Array.isArray(recommendations) ? recommendations : [];
    const primaryRecommendation = allRecommendations?.[0];

    const getPercentage = (text) => {
        if (!text) return 0;
        const textStr = String(text).toLowerCase();
        if (textStr.includes('high')) return 85;
        if (textStr.includes('medium')) return 60;
        if (textStr.includes('low')) return 35;
        const match = textStr.match(/(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
    };

    const getVisaRiskForCollege = () => visaAndSOP ? getPercentage(visaAndSOP.rejectionRiskPercentReasons) : 0;

    const renderRecommendations = () => {
        if (!allRecommendations || allRecommendations.length === 0) {
            return <div className="data-card"><p>No college recommendations generated.</p></div>;
        }

        return (
            <div className="scroll-section-wrapper">
                {allRecommendations.map((college, index) => (
                    <div className="scroll-card-item" key={college?.college || index}>
                        <DetailedCollegeCard college={college} rank={index + 1} collegeAnalysis={collegeAnalysis} />
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <Helmet>
                <title>Premium AI Student Visa Report | Get Next Visa</title>
                <meta name="description" content={`Premium 5-college AI report for ${studentInfo?.name || 'a student'}.`} />
            </Helmet>

            <CounsellingModal show={showCounsellingPopup} onClose={() => setShowCounsellingPopup(false)} />

            <div id="pdf-capture-wrapper">
                <div id="report-container" className={`ai-report-wrapper ${isMounted ? 'mounted' : ''}`}>
                    {/* ... The rest of your JSX remains exactly the same ... */}
                    <header className="report-header">
                        <div className="report-header__content">
                            <div className="report-header__brand"><h2 className="report-header__tagline">Premium Immigration & Education Report</h2></div>
                            <div className="report-header__actions">
                                <button className="action-button secondary-action" onClick={() => setShowCounsellingPopup(true)}><i className='bx bxs-calendar-plus' style={{ marginRight: '8px' }}></i>Book a Counselling</button>
                                {/* <button className="action-button" onClick={handleDownloadReport} disabled={isDownloadingPdf}>{isDownloadingPdf ? <><i className='bx bx-loader-alt bx-spin' style={{ marginRight: '8px' }}></i>Downloading...</> : <><i className='bx bxs-file-pdf' style={{ marginRight: '8px' }}></i>Download Report</>}</button> */}
                            </div>
                        </div>
                    </header>
                    <div className="report-main-grid">
                        <aside className="report-main-grid__side-column--left">
                            {studentInfo && <div className="data-card student-profile-card" style={{ '--i': 0 }}>
                                <div className="student-profile-card__header">
                                    <img src={studentInfo?.avatarUrl || studentImage} alt={studentInfo?.name || 'Student'} />
                                    <h3 className="student-profile-card__name">{studentInfo?.name || 'Student Name'}</h3>
                                </div>
                                <div className="student-profile-card__body">
                                    <div className="profile-section"><h4 className="profile-section__title"><i className='bx bxs-graduation'></i> Academic & Test Scores</h4>
                                        <ul className="profile-detail-list">
                                            <li><span className="academic-label">Completed</span><strong>{studentInfo.completedCourse || 'N/A'}</strong></li>
                                            <li><span className="academic-label">Stream</span><strong>{studentInfo.academicBackground || 'N/A'}</strong></li>
                                            {(() => {
                                                const exams = [['PTE Score', studentInfo.pteScore], ['TOEFL Score', studentInfo.toeflScore], ['IELTS Score', studentInfo.ieltsScore]];
                                                const valid = exams.filter(([, score]) => score != null && score !== '');
                                                if (valid.length === 0) return <li><span className="academic-label">Exam</span><strong>N/A</strong></li>;
                                                return valid.map(([label, score]) => (<li key={label}><span className="academic-label">{label}</span><strong>{score}</strong></li>));
                                            })()}
                                        </ul>
                                    </div>
                                    <div className="profile-section"><h4 className="profile-section__title"><i className='bx bxs-wallet'></i>Financials</h4>
                                        <div className="financials-list">
                                            <p><strong>Budget:</strong> {studentInfo.budget || studentInfo.budgetRange || 'N/A'}</p>
                                            <p><strong>Bank Balance (Liquid):</strong> {studentInfo.familyBankBalance?.liquid || 'N/A'}</p>
                                            <p><strong>Bank Balance (Fixed Deposit):</strong> {studentInfo.familyBankBalance?.fixedDeposit || 'N/A'}</p>
                                        </div>
                                    </div>
                                    {(studentInfo.father || studentInfo.mother) && (<div className="profile-section"><h4 className="profile-section__title"><i className='bx bxs-user'></i>Family Background</h4><ul className="profile-detail-list">{[["Father’s Employment", studentInfo.father?.employmentStatus], ["Father’s Experience", studentInfo.father?.experience], ["Mother’s Employment", studentInfo.mother?.employmentStatus], ["Mother’s Experience", studentInfo.mother?.experience]].map(([label, value]) => (<li key={label}><span className="academic-label">{label}</span><strong>{value || "N/A"}</strong></li>))}</ul></div>)}
                                    {familyBackgroundFinancialViability && (
                                        <div className="profile-section">
                                            <h4 className="profile-section__title">
                                                <i className='bx bxs-bank'></i>Financial Viability
                                            </h4>
                                            <ul className="profile-detail-list financial-viability">
                                                <li className="full-width-item">
                                                    <span>Assessment</span>
                                                    <p>{familyBackgroundFinancialViability.bankBalanceViabilityCheck || 'N/A'}</p>
                                                </li>
                                                {Array.isArray(familyBackgroundFinancialViability.recommendations) &&
                                                    familyBackgroundFinancialViability.recommendations.map((rec, i) => (
                                                        <li className="full-width-item" key={i}>
                                                            <span>Recommendation</span>
                                                            <p>{rec}</p>
                                                        </li>
                                                    ))}
                                            </ul>
                                        </div>
                                    )}

                                </div>
                            </div>}

                            {visaAndSOP && <div className="data-card" style={{ '--i': 1 }}><h3 className="data-card__title"><i className='bx bxs-file-blank'></i>Visa Documentation</h3><div className="visa-details"><p><span className="text-accent">Visa Type:</span> {visaAndSOP?.visaType || 'N/A'}</p><h4 className="data-card__subtitle">Document Checklist</h4><ul className='icon-list'>{Array.isArray(visaAndSOP?.documentChecklist) && visaAndSOP.documentChecklist.map(doc => (<li key={doc}><i style={{ color: "var(--success-color)" }} className='bx bx-check-square'></i>{doc}</li>))}</ul></div></div>}

                            {accommodationInsights && (
                                <div className="data-card accommodation-insights" style={{ '--i': 2 }}>
                                    <h3 className="data-card__title">
                                        <i className='bx bx-home'></i>
                                        Accommodation Insights
                                    </h3>
                                    <ul className="profile-detail-list">
                                        <li className="full-width-item">
                                            <span>Best Areas</span>
                                            <p>{accommodationInsights.bestAreasToLive || 'N/A'}</p>
                                        </li>
                                        <li className="full-width-item">
                                            <span>Safety Score</span>
                                            <p>{accommodationInsights.safetyScore || 'N/A'}</p>
                                        </li>
                                        <li className="full-width-item">
                                            <span>Tips</span>
                                            <p>{accommodationInsights.housingPlatformTips || 'N/A'}</p>
                                        </li>
                                    </ul>
                                </div>
                            )}

                            {backupPlan && (<div className="data-card data-card--backup" style={{ '--i': 3 }}><h3 className="data-card__title"><i className="bx bx-shield-quarter" /> Recommended Backup Plan</h3><p><span className="backup-key">Course/College:</span><span className="backup-value">{backupPlan.courseAndCollege}</span></p><p><span className="backup-key">Why it's safer:</span><span className="backup-value"> {backupPlan.reasonSaferPractical}</span></p><p><span className="backup-key">PR Route:</span><span className="backup-value"> {backupPlan.prRoute}</span></p></div>)}

                            {postStudyWorkAndPR && (<div className="data-card" style={{ '--i': 4 }}><h3 className="data-card__title"><i className="bx bxs-id-card"></i>Post-Study Work & PR</h3><div className="qna-list-container">{Object.entries(postStudyWorkAndPR).map(([rawKey, val]) => { const label = rawKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()); return (<div key={rawKey} className="qna-item"><p className="qna-question">{label}</p><p className="qna-answer">{val || 'N/A'}</p></div>); })}</div></div>)}

                            {peerInfluence && (<div className="data-card" style={{ '--i': 5 }}><h3 className="data-card__title"><i className='bx bxs-group'></i>Peer & Parental Influence</h3><div className="qna-list-container">{Object.entries(peerInfluence).map(([rawKey, val]) => { const label = rawKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()); return (<div key={rawKey} className="qna-item"><p className="qna-question">{label}</p><p className="qna-answer">{val || 'N/A'}</p></div>); })}</div></div>)}

                            {careerAlignment && (<div className="data-card" style={{ '--i': 6 }}><h3 className="data-card__title"><i className='bx bxs-briefcase'></i> Career Alignment</h3><div className="qna-list-container">{Object.entries(careerAlignment).map(([rawKey, val]) => { const label = rawKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()); return (<div key={rawKey} className="qna-item"><p className="qna-question">{label}</p><p className="qna-answer">{Array.isArray(val) ? val.join(', ') : val}</p></div>); })}</div></div>)}

                            {admissionRequirements && (<div className="data-card" style={{ '--i': 7 }}><h3 className="data-card__title"><i className='bx bxs-id-card'></i> Admission Requirements</h3><div className="qna-list-container">{Object.entries(admissionRequirements).map(([rawKey, val]) => { const label = rawKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()); return (<div key={rawKey} className="qna-item"><p className="qna-question">{label}</p><p className="qna-answer">{val}</p></div>); })}</div></div>)}
                        </aside>

                        <main className="report-main-grid__center-column">
                            {renderRecommendations()}

                            {scholarshipDetails?.length > 0 && <div style={{ '--i': allRecommendations.length }}><ScholarshipCard scholarships={scholarshipDetails} /></div>}

                            {allRecommendations.length > 0 && visaAndSOP && <div className="data-card" style={{ '--i': allRecommendations.length + 1 }}><h3 className="data-card__title"><i className='bx bx-pie-chart-alt-2'></i>Key Metrics Overview</h3><div className="metrics-card__grid">{allRecommendations.map((college, index) => (<div className="metrics-card__grid-item" key={index}><h4>{college?.college || 'N/A'}</h4><div className="donut-graph-container"><div className="donut-graph-item"><div className="donut-graph-viz" data-percent={`${getPercentage(college?.successProbability)}%`} style={{ '--p': isMounted ? getPercentage(college?.successProbability) : 0, '--c': 'var(--success-color)' }} /><p className="donut-graph__label">Success</p></div><div className="donut-graph-item"><div className="donut-graph-viz" data-percent={`${getVisaRiskForCollege()}%`} style={{ '--p': isMounted ? getVisaRiskForCollege() : 0, '--c': 'var(--danger-color)' }} /><p className="donut-graph__label">Visa Risk</p></div></div></div>))}</div><small className="disclaimer">*Visa risk is an overall country/profile estimate.</small></div>}

                            {visaAndSOP && <div className="data-card" style={{ '--i': allRecommendations.length + 2 }}><h3 className="data-card__title"><i className='bx bx-pen'></i>SOP & Profile Analysis</h3><h4 className="data-card__subtitle">Profile Improvement Suggestions</h4><ul className='suggestion-list'>{Array.isArray(visaAndSOP?.improvementSuggestions) && visaAndSOP.improvementSuggestions.map((s, i) => (<li key={i}>{s}</li>))}</ul>{Array.isArray(visaAndSOP?.sopSuggestions) && visaAndSOP.sopSuggestions.length > 0 && (<><h4 className="data-card__subtitle" style={{ marginTop: '1.5rem' }}>Specific SOP Suggestions</h4><ul className='suggestion-list'>{visaAndSOP.sopSuggestions.map((s, i) => (<li key={i}>{s}</li>))}</ul></>)}<h4 className="data-card__subtitle" style={{ marginTop: '1.5rem' }}>SOP Excerpt</h4><blockquote className={`sop-card__excerpt ${isSopExpanded ? 'expanded' : ''}`}>{visaAndSOP?.sopExcerpt || 'No SOP excerpt available.'}</blockquote>{(visaAndSOP?.sopExcerpt || '').length > 250 && (<button onClick={toggleSopExpansion} className="expand-text-button">{isSopExpanded ? 'Read Less' : 'Read More'}</button>)}</div>}

                            {finalComment && <div className="data-card data-card--verdict" style={{ '--i': allRecommendations.length + 3 }}><h3 className="data-card__title"><i className='bx bxs-comment-error'></i>Analyst's Final Verdict</h3><p>{finalComment}</p></div>}

                        </main>

                        <aside className="report-main-grid__side-column--right">
                            {(() => {
                                let i = 0;
                                const formatStackedKey = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                                return (
                                    <>
                                        {(primaryRecommendation || fullCostOfLiving) && <div style={{ '--i': i++ }}><CostAnalysis fullCostOfLiving={fullCostOfLiving} educationCosts={educationCosts} primaryRecommendation={primaryRecommendation} totalFirstYearInr={livingCosts?.totalFirstYear?.inr} investmentPlanning={timelineInvestmentPlanning} /></div>}

                                        {jobMarket && <div className="data-card" style={{ '--i': i++ }}><h3 className="data-card__title"><i className='bx bx-briefcase-alt-2'></i>Job Market Snapshot</h3><h4 className="data-card__subtitle">Full-Time Roles</h4>{jobMarket.roles.map((role, idx) => (<div key={idx} className="job-card__role-item qna-item"><h4>{role.title}</h4><p><span className="text-accent">Salary:</span> {role.salary.local} / {role.salary.inr}</p><p><span className="text-accent">Demand:</span> <span className={`status-badge ${role.demandLevel.toLowerCase()}`}>{role.demandLevel}</span></p><p><span className="text-accent">PR Support:</span> {role.prSupport}</p></div>))}{jobMarket.partTimeOpportunities.length > 0 && <><h4 className="data-card__subtitle" style={{ marginTop: '1.5rem' }}>Part-Time Opportunities</h4>{jobMarket.partTimeOpportunities.map((job, idx) => (<div key={idx} className="job-card__role-item qna-item"><h4>{job.role}</h4><p><span className="text-accent">Avg. Wage:</span> {job.wage.local} / {job.wage.inr}</p><p><span className="text-accent">Difficulty:</span> <span className={`status-badge ${job.findingDifficulty.toLowerCase()}`}>{job.findingDifficulty}</span></p></div>))}</>}</div>}

                                        {flightCostsTips && <div className="data-card flight-travel-tips" style={{ '--i': i++ }}><h3 className="data-card__title"><i className='bx bxs-plane-alt'></i>Flight & Travel Tips</h3><div style={{ marginTop: '1rem' }}><p><strong>Avg. Fare:</strong> {flightCostsTips.fromToAverageCost || 'N/A'}</p><p><strong>Peak/Off-Season:</strong> {flightCostsTips.peakOffSeasonInsights || 'N/A'}</p><p><strong>Best Booking Time:</strong> {flightCostsTips.bestBookingPeriod || 'N/A'}</p></div></div>}

                                        {roi && <div className="data-card return-on-investment" style={{ '--i': i++ }}><h3 className="data-card__title"><i className='bx bxs-coin-stack'></i>Return on Investment</h3><div style={{ marginTop: '1rem' }}><p><strong>Avg Grad Salary:</strong> {roi.averageGraduateSalary?.local || 'N/A'} / {roi.averageGraduateSalary?.inr || 'N/A'}</p><p><strong>Loan Repayment Ease:</strong> {roi.loanRepaymentEase}</p><p><strong>Earning While Studying:</strong> {roi.earningWhileStudying}</p></div></div>}

                                        {countrySafetyAndPreference && <div className="data-card country-safety-info" style={{ '--i': i++ }}><h3 className="data-card__title"><i className='bx bxs-flag-alt'></i>Country & Safety Info</h3><div style={{ marginTop: '1.rem' }}>{Object.entries(countrySafetyAndPreference).map(([key, value]) => value && (<p key={key}><strong>{formatStackedKey(key)}:</strong> {value}</p>))}</div></div>}

                                        {connectivityLifestyle && <div className="data-card travel-lifestyle" style={{ '--i': i++ }}><h3 className="data-card__title"><i className='bx bx-planet'></i>Travel & Lifestyle</h3><div style={{ marginTop: '1rem' }}>{Object.entries(connectivityLifestyle).map(([key, value]) => value && (<p key={key}><strong>{formatStackedKey(key)}:</strong> {String(value)}</p>))}</div></div>}

                                        {prPathway && (
                                            <div className="data-card" style={{ '--i': i++ }}>
                                                <h3 className="data-card__title"><i className="bx bx-line-chart"></i> PR Pathway Roadmap</h3>
                                                <ul className="pathway-card__timeline">
                                                    {Array.isArray(prPathway.timelineSteps) && prPathway.timelineSteps.map((item, index) => (
                                                        <li className="pathway-card__timeline-event" key={item?.step || index}>
                                                            <p className="step-title">{item?.step || 'N/A'}</p>
                                                            <p className="step-duration">{item?.duration || 'N/A'}</p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {predictedQnA && <div className="data-card" style={{ '--i': i++ }}><h3 className="data-card__title"><i className='bx bx-question-mark'></i>Predicted Q&A</h3>{Array.isArray(predictedQnA) && predictedQnA.map((item, index) => (<QnAItem item={item} key={index} />))}</div>}

                                        {finalActionPlan && <div className="data-card" style={{ '--i': i++ }}><h3 className="data-card__title"><i className='bx bx-list-check'></i>Final Action Plan</h3><h4 className="data-card__subtitle">Complete To-Do List</h4><ul className='icon-list'>{Array.isArray(finalActionPlan?.todoList) && finalActionPlan.todoList.map((step, idx) => (<li key={idx}><i style={{ color: "var(--primary-color)" }} className='bx bx-checkbox'></i>{step}</li>))}</ul><h4 className="data-card__subtitle" style={{ marginTop: '1.5rem' }}>Immediate Next 3 Steps</h4><ol className="suggestion-list">{Array.isArray(finalActionPlan?.next3Steps) && finalActionPlan.next3Steps.map((step, idx) => (<li key={idx}>{step}</li>))}</ol></div>}

                                        {footerResources && <div className="data-card" style={{ '--i': i++ }}><h3 className="data-card__title"><i className='bx bx-link'></i>Helpful Resources</h3><ul className='icon-list'>{footerResources?.resumeBuilderLink && <li><i className='bx bx-file'></i><a href={footerResources.resumeBuilderLink} target="_blank" rel="noopener noreferrer">Resume Builder</a></li>}{footerResources?.sopGeneratorLink && <li><i className='bx bx-pen'></i><a href={footerResources.sopGeneratorLink} target="_blank" rel="noopener noreferrer">SOP Generator</a></li>}{Array.isArray(footerResources?.accommodationPlatforms) && footerResources.accommodationPlatforms.map(link => { const href = link.startsWith('http') ? link : `https://${link}`; return (<li key={link}><i className='bx bx-home'></i><a href={href} target="_blank" rel="noopener noreferrer">Accommodation</a></li>); })}{footerResources?.budgetCalculatorLink && <li><i className='bx bxs-calculator'></i><a href={footerResources.budgetCalculatorLink} target="_blank" rel="noopener noreferrer">Budget Calculator</a></li>}</ul></div>}

                                    </>
                                );
                            })()}
                        </aside>
                    </div>
                </div>
            </div >
        </>
    );
};

export default AIReportDisplay3;